import {
  MatterPartyInsuranceSection,
  MatterPartyVehiclesSection,
} from "@/components/features/matter/information/MatterInformationDefendantSection";
import { LinkedMatterContactConnectionMembershipPopover } from "@/components/features/matter/shared/LinkedMatterMembershipPopover";
import {
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  Text,
} from "@/components/ui";
import { MatterContactConnectionRichCard } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { ServerErrorFallbackCard } from "@/components/ui/ServerErrorFallback/ServerErrorFallbackCard";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useMatterInsuranceVehiclePermissions } from "@/hooks/useMatterInsuranceVehiclePermissions";
import { useDeleteConnection } from "@/lib/data/react-query/connections/connection";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter, type MatterContactConnection } from "@/lib/types";
import { getRemoveMatterContactConnectionModalCopy } from "@/lib/utils/matterContactConnectionHelpers";
import { matterContactConnectionHeroToneFromMatterConnection } from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import { getOtherPartyMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { ErrorBoundary } from "@sentry/react";
import { PlusCircle, UserIcon } from "lucide-react";
import { useCallback, useState } from "react";

interface MatterInformationOtherPartiesSectionProps {
  matter: Matter;
}

const OTHER_PARTY_LABEL_OPTIONS: MatterContactConnection["label"][] = [
  "Witness",
  "Expert",
  "Third Party",
  "Other",
];

const MatterInformationOtherPartiesSectionDangerous = ({
  matter,
}: MatterInformationOtherPartiesSectionProps) => {
  const { openMatterContactConnectionModal } = useGlobalModal();

  const { hasActionPermission } = useUser();
  const hasCreateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasUpdateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeleteMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );

  const {
    hasCreateInsurancePermission,
    hasViewInsurancePermission,
    hasUpdateInsurancePermission,
    hasCreateVehiclePermission,
    hasViewVehiclePermission,
    hasUpdateVehiclePermission,
  } = useMatterInsuranceVehiclePermissions();

  const deleteMatterContactConnection = useDeleteConnection();

  const [deleteMatterContactConfirmation, setDeleteMatterContactConfirmation] =
    useState<{
      matterContactConnectionId: string;
      connectionRoleLabel: string;
    } | null>(null);

  const requestDeleteOtherPartyConnection = useCallback(
    (matterContactConnectionId: string, connectionRoleLabel: string) => {
      setDeleteMatterContactConfirmation({
        matterContactConnectionId,
        connectionRoleLabel,
      });
    },
    [],
  );

  const confirmDeleteOtherPartyConnection = useCallback(async () => {
    const pending = deleteMatterContactConfirmation;
    if (!pending) {
      return;
    }
    try {
      await deleteMatterContactConnection.mutateAsync({
        connectionId: pending.matterContactConnectionId,
        matterId: matter.id,
      });
    } finally {
      setDeleteMatterContactConfirmation(null);
    }
  }, [
    deleteMatterContactConfirmation,
    deleteMatterContactConnection,
    matter.id,
  ]);

  const onDeleteOtherParty = hasDeleteMatterContactConnectionPermission
    ? requestDeleteOtherPartyConnection
    : undefined;
  const deleteMatterContactModalCopy = deleteMatterContactConfirmation
    ? getRemoveMatterContactConnectionModalCopy(
        deleteMatterContactConfirmation.connectionRoleLabel,
      )
    : null;

  const openCreateOtherParty = () => {
    openMatterContactConnectionModal({
      matterId: matter.id,
      labelOptions: OTHER_PARTY_LABEL_OPTIONS,
      autoOpenContactSelection: true,
      title: "Add other party",
      createPresentation: "modal",
    });
  };

  const otherPartyConnections = getOtherPartyMatterContactConnections(matter);

  return (
    <div className="grid grid-cols-1 gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <Text variant="title">Other parties</Text>
        {hasCreateMatterContactConnectionPermission && (
          <Button
            size="sm"
            onClick={openCreateOtherParty}
            className="md:self-start"
          >
            <PlusCircle className="w-4" />
            Add other party
          </Button>
        )}
      </div>

      {otherPartyConnections.length === 0 ? (
        <Card className="rounded-[24px] border">
          <CardContent className="flex min-h-[260px] flex-col items-center justify-center gap-4 text-center">
            <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-2xl">
              <UserIcon className="text-muted-foreground h-7 w-7" />
            </div>
            <div className="space-y-1">
              <Text variant="body" className="font-semibold">
                No other parties added yet.
              </Text>
              <Text variant="subHeading" color="muted">
                Add a witness, expert, third party, or other contact to track
                them on this matter.
              </Text>
            </div>
            {hasCreateMatterContactConnectionPermission && (
              <Button
                size="sm"
                onClick={openCreateOtherParty}
                variant="outline"
              >
                <PlusCircle className="w-4" />
                Create other party
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        otherPartyConnections.map((otherParty: MatterContactConnection) => {
          const otherPartyConnectionId = otherParty.id;
          const otherPartyContactId = otherParty.contactId;
          const otherPartyLabel = otherParty.label;
          const witnessStatusChip =
            otherPartyLabel === "Witness" && otherParty.witnessStatus
              ? [otherParty.witnessStatus]
              : undefined;

          return (
            <Card
              key={otherPartyConnectionId}
              className="overflow-hidden rounded-[24px] border shadow-sm"
            >
              <CardContent className="p-0">
                <div className="grid gap-0 xl:grid-cols-[380px_minmax(0,1fr)]">
                  <div className="border-border/60 bg-card border-r p-6">
                    <MatterContactConnectionRichCard
                      contact={otherParty.contact}
                      matterContactConnection={otherParty}
                      matterId={matter.id}
                      communicationActions="call-text"
                      tone={matterContactConnectionHeroToneFromMatterConnection(
                        matter,
                        otherParty,
                      )}
                      highlightedBadges={witnessStatusChip}
                      titleAccessory={
                        <LinkedMatterContactConnectionMembershipPopover
                          contactId={otherPartyContactId}
                          currentMatterId={matter.id}
                          relatedMatterIds={matter.relatedMatterConnections.map(
                            (connection) => connection.relatedMatterId,
                          )}
                        />
                      }
                      maxVisibleBadges={3}
                      showEditButton={
                        hasUpdateMatterContactConnectionPermission
                      }
                      onEdit={
                        hasUpdateMatterContactConnectionPermission
                          ? () => {
                              openMatterContactConnectionModal({
                                matterId: matter.id,
                                labelOptions: OTHER_PARTY_LABEL_OPTIONS,
                                autoOpenContactSelection: false,
                                title: "Edit other party information",
                                matterContactConnection: otherParty,
                                matterContactConnectionContactId:
                                  otherPartyContactId,
                                editing: true,
                                editPresentation: "modal",
                              });
                            }
                          : undefined
                      }
                      editButtonTestId={
                        hasUpdateMatterContactConnectionPermission
                          ? `edit-other-party-contact-button-${otherPartyLabel}`
                          : undefined
                      }
                      onDelete={
                        onDeleteOtherParty
                          ? (matterContactConnectionId: string) => {
                              onDeleteOtherParty(
                                matterContactConnectionId,
                                otherPartyLabel,
                              );
                            }
                          : undefined
                      }
                      className="min-h-full w-full min-w-0 border-0 p-0"
                    />
                  </div>

                  <div className="bg-card p-6 lg:p-8">
                    <div className="space-y-7">
                      {matter.incidentType?.insuranceModuleAccess !== false && (
                        <MatterPartyInsuranceSection
                          matter={matter}
                          partyConnection={otherParty}
                          partyRole="OtherParty"
                          canViewInsurance={hasViewInsurancePermission}
                          canCreateInsurance={hasCreateInsurancePermission}
                          canEditInsurance={hasUpdateInsurancePermission}
                        />
                      )}
                      {/* TODO: Gate on `matter.incidentType?.vehicleModuleAccess` once vehicle module access is fully implemented */}
                      <MatterPartyVehiclesSection
                        matter={matter}
                        partyConnection={otherParty}
                        partyRole="OtherParty"
                        canViewVehicles={hasViewVehiclePermission}
                        canCreateVehicle={hasCreateVehiclePermission}
                        canEditVehicle={hasUpdateVehiclePermission}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      <ActionConfirmationModal
        open={deleteMatterContactConfirmation != null}
        setOpen={(open) => {
          if (!open && !deleteMatterContactConnection.isPending) {
            setDeleteMatterContactConfirmation(null);
          }
        }}
        title={deleteMatterContactModalCopy?.title}
        description={deleteMatterContactModalCopy?.description}
        buttonText={deleteMatterContactModalCopy?.buttonText}
        isActionPending={deleteMatterContactConnection.isPending}
        afterActionFn={() => {
          void confirmDeleteOtherPartyConnection();
        }}
      />
    </div>
  );
};

export function MatterInformationOtherPartiesSection(
  props: MatterInformationOtherPartiesSectionProps,
) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <MatterInformationOtherPartiesSectionDangerous {...props} />
    </ErrorBoundary>
  );
}
